import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useDarkMode } from "../../contexts/DarkModeContext";
import { 
  fetchTeacherProfile, 
  fetchTeacherLessons, 
  fetchTeacherRatings, 
  submitTeacherRating,
  clearTeacherDetails 
} from "../../redux/slices/teacherDetailsSlice";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./TeacherDetails.css";

function TeacherDetails() {
  const { isDarkMode } = useDarkMode();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState("lessons");
  const [rating, setRating] = useState(3);
  const [comment, setComment] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [selectedRating, setSelectedRating] = useState(3);

  const { 
    teacherProfile, 
    teacherLessons, 
    teacherRatings,
    profileLoading, 
    lessonsLoading, 
    ratingsLoading,
    submitRatingLoading,
    profileError, 
    lessonsError, 
    ratingsError,
    submitRatingError,
    lessonsPagination,
    currentLessonsPage
  } = useSelector((state) => state.teacherDetails);

  // Check authentication
  const isAuthenticated = !!localStorage.getItem("token3almny");

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    window.scrollTo(0, 0);
    
    // Clear previous data
    dispatch(clearTeacherDetails());
    
    // Fetch teacher data
    if (id) {
      dispatch(fetchTeacherProfile(id));
      dispatch(fetchTeacherLessons({ teacherId: id, page: 1, perPage: 15 }));
      dispatch(fetchTeacherRatings(id));
    }

    // Add intersection observer for animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [dispatch, id, isAuthenticated, navigate]);

  // Handle rating submission
  const handleSubmitRating = async (e) => {
    e.preventDefault();
    
    if (!comment.trim()) {
      toast.error("يرجى كتابة تعليق");
      return;
    }

    try {
      await dispatch(submitTeacherRating({
        comment: comment.trim(),
        teacher_id: parseInt(id),
        rate: rating
      })).unwrap();
      
      toast.success("تم إرسال تقييمك بنجاح");
      setComment("");
      setRating(3);
      
      // Refresh ratings
      dispatch(fetchTeacherRatings(id));
    } catch (error) {
      toast.error(error || "حدث خطأ في إرسال التقييم");
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Handle pagination for lessons
  const handleLoadMoreLessons = () => {
    if (lessonsPagination && currentLessonsPage < lessonsPagination.pages_count) {
      dispatch(fetchTeacherLessons({ 
        teacherId: id, 
        page: currentLessonsPage + 1, 
        perPage: 15 
      }));
    }
  };

  // Loading states
  if (profileLoading) {
    return (
      <div className={`teacher-details-page ${isDarkMode ? "dark-mode" : "light-mode"}`}>
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner">جاري تحميل بيانات المدرس...</div>
          </div>
        </div>
      </div>
    );
  }

  // Error states
  if (profileError) {
    return (
      <div className={`teacher-details-page ${isDarkMode ? "dark-mode" : "light-mode"}`}>
        <div className="container">
          <div className="error-container">
            <div className="error-message">
              <p>حدث خطأ في تحميل بيانات المدرس</p>
              <p>{profileError}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="teacher-details-profile">
      <div className={`teacher-details-page ${isDarkMode ? "dark-mode" : "light-mode"}`}>
        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={true}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={isDarkMode ? "dark" : "light"}
        />

        {/* Floating Background Elements */}
        <div className="floating-elements">
          <div className="floating-element">📚</div>
          <div className="floating-element">✏️</div>
          <div className="floating-element">📖</div>
          <div className="floating-element">🖊️</div>
          <div className="floating-element">📝</div>
          <div className="floating-element">🎓</div>
        </div>

        <div className="container">
        {/* Breadcrumb */}
        <div className="lessons-breadcrumb ">
          <h1 className="breadcrumb-title">تفاصيل المدرس</h1>
          <div className="breadcrumb-links2">
            <span>الرئيسية</span> <span className="breadcrumb-separator">/</span> 
            <span className="breadcrumb-current">المدرسين</span>
          </div>
        </div>

        {/* Teacher Info */}
        {teacherProfile && (
          <div className="teacher-header d-flex justify-content-between align-items-center flex-wrap ">
            <div>
              <h3 className="fw-bold">{teacherProfile.name || "مدرس"}</h3>
              <div className="fw-bold mb-2">{teacherProfile.subject_name || "مادة دراسية"}</div>
              <div className="text-warning small">
                {teacherProfile.about || "مدرس متميز"}
              </div>
              <div className="rating mt-2">
                ⭐ {teacherProfile.average_rate || 0} 
                <span className="text-muted">({teacherProfile.rate_count || 0})</span>
              </div>
              <div className="teacher-stats mt-2">
                <span className="stat-item">👥 {teacherProfile.students_count || 0} طالب</span>
                <span className="stat-item">📚 {teacherProfile.lessons_count || 0} درس</span>
                <span className="stat-item">❤️ {teacherProfile.loves_count || 0} إعجاب</span>
              </div>
            </div>
            <img 
              src={teacherProfile.prof_img || "/user.png"} 
              alt="Teacher" 
              className="teacher-img" 
              style={{objectFit: "cover"}}
              onError={(e) => {
                e.target.src = "/user.png";
              }}
            />
          </div>
        )}

        {/* Tabs */}
        <div className="tabs mt-4 animate-on-scroll">
          <button 
            className={`btn ${activeTab === "lessons" ? "btn-primary" : "btn-light"}`} 
            onClick={() => handleTabChange("lessons")}
          >
            الحصص الدراسية
          </button>
          <button 
            className={`btn ${activeTab === "reviews" ? "btn-primary" : "btn-light"}`} 
            onClick={() => handleTabChange("reviews")}
          >
            التقييمات
          </button>
        </div>

        {/* Content */}
        <div className="tab-content mt-4">
          {activeTab === "lessons" ? (
            <div className="lessons-section">
              {lessonsLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner">جاري تحميل الدروس...</div>
                </div>
              ) : lessonsError ? (
                <div className="error-container">
                  <div className="error-message">
                    <p>حدث خطأ في تحميل الدروس</p>
                    <p>{lessonsError}</p>
                  </div>
                </div>
              ) : teacherLessons.length > 0 ? (
                <>
                  <div className="lessons-grid">
                    {teacherLessons.map((lesson, index) => (
                      <Link to={`/lessonDetails/${lesson.id}`} key={lesson.id} className="text-decoration-none">
                        <div className="lesson-card shadow-sm">
                          <div className="lesson-header" style={{ backgroundImage: `url('${lesson.image || '/courseimage.png'}')` }}>
                            <span className="badge-status">غير مشترك</span>
                          </div>
                          <div className="lesson-body">
                            <div className="rating">({lesson.reviews_count || 0}) {"★".repeat(lesson.rating || 0)}</div>
                            <h5 className="mb-4">{lesson.title}</h5>
                            <div className="lesson-info">
                              👥 {lesson.students_count || 0} طالب | 📄 {lesson.files_count || 0} ملفات | 🎥 فيديو شرح
                            </div>
                          </div>
                          <div className="lesson-footer">
                            <div className="teacher">
                              <img src={teacherProfile?.image || "/user.png"} alt="" className="teacher-img-sm" /> 
                              {teacherProfile?.name || "مدرس"}
                            </div>
                            <span>{teacherProfile?.subject_name || "مادة دراسية"}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  
                  {/* Load More Button */}
                  {lessonsPagination && currentLessonsPage < lessonsPagination.pages_count && (
                    <div className="text-center mt-4">
                      <button 
                        className="btn-show-more"
                        onClick={handleLoadMoreLessons}
                        disabled={lessonsLoading}
                      >
                        {lessonsLoading ? "جاري التحميل..." : "عرض المزيد ⬇️"}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-data-container">
                  <p>لا توجد دروس متاحة لهذا المدرس</p>
                </div>
              )}
            </div>
          ) : (
            <div className="reviews-section">
              {ratingsLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner">جاري تحميل التقييمات...</div>
                </div>
              ) : ratingsError ? (
                <div className="error-container">
                  <div className="error-message">
                    <p>حدث خطأ في تحميل التقييمات</p>
                    <p>{ratingsError}</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Reviews List */}
                  <div className="lesson-reviews">
                    {teacherRatings.length > 0 ? (
                      teacherRatings.map((review, index) => (
                        <div className="review-box d-flex gap-2 mb-3" key={review.id || index}>
                          <img src="/user.png" className="review-avatar rounded-circle" alt="user" />
                          <div className="review-content">
                            <div className="review-header ">
                              <strong className="text-white reviewUserName">{review.user_name || "مستخدم"}</strong>
                            
                            </div>
                            <div className="text-warning review-stars">
                              {"⭐".repeat(review.rate || 0)}
                            </div>
                            <p className="review-text text-white">{review.comment}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-data-container">
                        <p>لا توجد تقييمات متاحة لهذا المدرس</p>
                      </div>
                    )}
                  </div>

                  {/* Add Review Form */}
                  <form className={`add-review-box ${isDarkMode ? 'dark-mode' : 'light-mode'}`} onSubmit={handleSubmitRating}>
                    <h6 className="fw-bold">ادخل تقييمك</h6>
                    <div className="stars stars-animated">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          className={`star-svg cursor-pointer ${selectedRating >= star ? "active" : ""} ${selectedRating === star ? "bounce" : ""}`}
                          onClick={() => {
                            setSelectedRating(star);
                            setRating(star);
                          }}
                          title={`${star} نجمة`}
                        >
                          <svg width="28"  height="28" viewBox="0 0 24 24" fill={selectedRating >= star ? '#ffc107' : '#ddd'} stroke="#ffc107" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{transition: 'fill 0.2s', cursor: "pointer"}}>
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                        </span>
                      ))}
                      <span className="rating-text ms-2 text-white">
                        {selectedRating === 1 ? "نجمة واحدة" : 
                         selectedRating === 2 ? "نجمتان" : 
                         selectedRating === 3 ? "3 نجوم" : 
                         selectedRating === 4 ? "4 نجوم" : 
                         selectedRating === 5 ? "5 نجوم" : "اختر التقييم"}
                      </span>
                    </div>
                    <h6 className="fw-bold">اكتب تعليقك</h6>
                    <textarea 
                      className="form-control mb-3" 
                      rows="3" 
                      value={comment} 
                      onChange={(e) => setComment(e.target.value)} 
                      placeholder="اكتب التعليق"
                      disabled={submitRatingLoading}
                    />
                    <button 
                      className="btn btn-primary px-4" 
                      type="submit"
                      disabled={submitRatingLoading}
                    >
                      {submitRatingLoading ? "جاري الإرسال..." : "إرسال ←"}
                    </button>
                  </form>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
  );
}

export default TeacherDetails;
